import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Inbox01, RefreshCw01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Table } from "@/components/application/table/table";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { blockedActionProps } from "@/components/app/blockedAction";
import { executeApiTool, type ExecuteResponse, type PendingTransactionItem } from "@/lib/apiTools";
import { formatDateTime } from "@/lib/format";

/** Fronta patří vždy jednomu tokenu — proto je jeho id součástí stavu, ne jen položky. */
export interface PendingQueueState {
  tokenId: string;
  items: PendingTransactionItem[];
}

export const EMPTY_PENDING_QUEUE: PendingQueueState = { tokenId: "", items: [] };

/**
 * Jak se fronta mění po doběhnutí operace v API nástrojích.
 *
 * Čistá funkce, protože je to jediné místo, kde se rozhoduje, co ve frontě zůstane —
 * a rozhodnutí „vyřízená transakce zmizí" se dá otestovat bez celé stránky.
 *
 * Vrací **beze změny tentýž objekt**, když se operace fronty netýká; React tak
 * překreslení přeskočí.
 */
export function pendingQueueAfter(
  prev: PendingQueueState,
  opId: string,
  res: ExecuteResponse,
  values: Record<string, unknown>,
): PendingQueueState {
  if (!res.envelope.success) return prev;
  const tokenId = String(values.tokenId ?? "");

  if (opId === "pendingTransactions") {
    return { tokenId, items: (res.envelope.result as PendingTransactionItem[]) ?? [] };
  }

  // Vyřízená transakce z fronty zmizí. Auth server ji v dalším výpisu už nevrátí, takže by
  // na seznamu visela jako čekající, dokud si někdo nevzpomene obnovit — a sváděla by
  // ke druhému schválení téže operace. Fronta přitom patří jednomu tokenu, takže operace
  // nad jiným tokenem do ní nemá co mluvit.
  if (opId !== "approveTransaction" && opId !== "rejectTransaction") return prev;
  const handled = String(values.transactionId ?? "");
  if (!handled || !tokenId || prev.tokenId !== tokenId) return prev;

  const items = prev.items.filter((i) => i.transactionId !== handled);
  return items.length === prev.items.length ? prev : { ...prev, items };
}

/**
 * Vyčtená fronta čekajících transakcí pro Privat banku.
 *
 * Stahování transakcí a schválení jedné z nich bylo přes samotný formulář nepohodlné:
 * operace `pendingTransactions` vypsala JSON envelope a uživatel z ní musel ručně vyzobat
 * `transactionId` a zašifrovanou zprávu, přepnout tab a obojí opsat. Panel to dělá za něj.
 *
 * **Tlačítka nevolají operaci přímo** (na rozdíl od stejné fronty u eChannels) — přepnou
 * na příslušný tab a předvyplní parametry, takže je před odesláním vidět a jde upravit
 * volba hesla, `recognitionType` i režim. Přesměrování obstarává `goToOp` z `ConnectorRunner`.
 *
 * Stav fronty drží `ConnectorRunner`, ne tenhle komponent ani `OperationForm` — ten se při
 * každém přepnutí tabu remountuje, takže by fronta zmizela právě ve chvíli, kdy je potřeba.
 */
export function PrivatPendingQueuePanel({
  connectorId,
  tokenId,
  items,
  onRefreshed,
  goToOp,
}: {
  connectorId: string;
  tokenId: string;
  items: PendingTransactionItem[];
  onRefreshed: (items: PendingTransactionItem[]) => void;
  goToOp: (opId: string, values: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Vyčte frontu znovu a **vrátí formulář na záložku výpisu**.
   *
   * Samotné vyčtení šlo i bez přepnutí, jenže po *Schválit* stojí nad frontou formulář
   * *Schválit transakci* — obnovení se pak projeví jen dole a vypadá to, že tlačítko
   * uživatele odneslo do schvalování. Po obnovení tedy sedí nahoře výpis, ke kterému
   * fronta patří, a stav obrazovky souhlasí s tím, co se právě stalo.
   */
  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await executeApiTool(connectorId, "pendingTransactions", {
        tokenId,
        includeDetails: true,
        headers: { "Content-Type": "application/json" },
      });
      if (res.envelope.success) {
        onRefreshed((res.envelope.result as PendingTransactionItem[]) ?? []);
        goToOp("pendingTransactions", { tokenId });
      } else {
        setError(res.envelope.error ?? t("apitools.pending.refreshFailed"));
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <div className="flex flex-wrap items-center gap-2">
        <Inbox01 className="size-4 text-tertiary" />
        <h2 className="flex-1 text-md font-semibold text-primary">
          {t("apitools.pending.title", { count: items.length })}
        </h2>
        <span className="font-mono text-xs text-tertiary">{tokenId}</span>
        <Button size="sm" color="secondary" iconLeading={RefreshCw01} isLoading={refreshing} onPress={() => void refresh()}>
          {t("apitools.pending.refresh")}
        </Button>
      </div>

      <p className="text-xs text-tertiary">{t("apitools.pending.hint")}</p>
      {error && <p className="text-sm text-error-primary">{error}</p>}

      {items.length === 0 ? (
        <p className="rounded-lg bg-secondary p-4 text-sm text-tertiary">{t("apitools.pending.empty")}</p>
      ) : (
      <div className="overflow-hidden rounded-lg ring-1 ring-secondary">
        <Table aria-label={t("apitools.pending.title", { count: items.length })} selectionMode="none" size="sm">
          <Table.Header>
            <Table.Head isRowHeader id="transactionId" label={t("apitools.f.transactionId")} />
            <Table.Head id="expiresAt" label={t("apitools.pending.expiresAt")} />
            <Table.Head id="message" label={t("apitools.pending.message")} />
            <Table.Head id="actions" label="" />
          </Table.Header>
          <Table.Body items={items}>
            {(tx) => {
              // Bez zašifrované zprávy nelze podepsat: `secureMessage` je u schválení
              // povinný a server ho sám nedohledá. Prázdný prefill by skončil neprůhlednou
              // chybou až na serveru, proto se akce zablokuje i s vysvětlením.
              const blockedReason = tx.encryptedMessage ? undefined : t("apitools.pending.noMessage");
              const approve = (
                <Button
                  size="sm"
                  color="tertiary"
                  {...blockedActionProps(blockedReason)}
                  onPress={
                    blockedReason
                      ? undefined
                      : () =>
                          goToOp("approveTransaction", {
                            tokenId,
                            transactionId: tx.transactionId,
                            secureMessage: tx.encryptedMessage,
                          })
                  }
                >
                  {t("apitools.pending.approve")}
                </Button>
              );

              return (
                <Table.Row id={tx.transactionId}>
                  <Table.Cell className="font-mono text-secondary">
                    <span title={tx.transactionId}>{shorten(tx.transactionId)}</span>
                  </Table.Cell>
                  <Table.Cell className="text-tertiary">
                    {tx.expiresAt ? formatDateTime(tx.expiresAt) : "—"}
                  </Table.Cell>
                  <Table.Cell className="max-w-xs truncate text-tertiary">{preview(tx) ?? "—"}</Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button
                        size="sm"
                        color="tertiary"
                        onPress={() => goToOp("transactionDetail", { tokenId, transactionId: tx.transactionId })}
                      >
                        {t("apitools.pending.detail")}
                      </Button>
                      {blockedReason ? <Tooltip title={blockedReason} placement="bottom">{approve}</Tooltip> : approve}
                      <Button
                        size="sm"
                        color="tertiary-destructive"
                        // `reason` se záměrně nepředvyplňuje — Privat ho zahazuje
                        // (jejich tělo zamítnutí nese jen identifikátor operace), takže
                        // předvyplněný důvod by budil dojem, že se odesílá.
                        onPress={() => goToOp("rejectTransaction", { tokenId, transactionId: tx.transactionId })}
                      >
                        {t("apitools.pending.reject")}
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            }}
          </Table.Body>
        </Table>
      </div>
      )}
    </div>
  );
}

/** Identifikátory transakcí jsou dlouhé; v buňce by rozhodily layout. */
function shorten(value: string): string {
  return value.length > 20 ? `${value.slice(0, 20)}…` : value;
}

/**
 * Jednořádkový náhled zprávy. `wysiwygData` je víceřádkový výpis `klíč: hodnota`, takže
 * se konce řádků slijí do mezer; při `includeDetails=false` náhled prostě není.
 */
function preview(tx: PendingTransactionItem): string | undefined {
  const w = tx.transaction?.wysiwygData;
  if (!w) return undefined;
  const line = w.split("\n").map((l) => l.trim()).filter((l) => l && !l.startsWith("===")).join(" · ");
  return line || undefined;
}
